export type Volume = {
  roman: string;
  title: string;
  intro: string;
  listLabel: string;
  list: string[];
  extraLabel?: string;
  extra?: string[];
  note?: string;
  goal: string;
  color: string;
};

export const volumes: Volume[] = [
  {
    roman: 'I',
    title: 'Start With the Functional Concern',
    intro:
      'Before exploring possible explanations for a child’s motor performance, you need to understand the actual activity or participation difficulty you’re trying to investigate. I’ll show you how to organize the initial concern without immediately assigning a praxis-related interpretation.',
    listLabel: 'You’ll learn to structure:',
    list: [
      'The family’s primary concern.',
      'The child’s functional difficulties during everyday activities.',
      'Activities and routines affected.',
      'Relevant task demands.',
      'Environmental and contextual factors.',
      'Previous experiences with similar activities.',
      'Initial motor observations.',
      'Questions that need clarification.',
    ],
    note: 'You’ll also learn to distinguish the functional problem from the possible mechanisms that might contribute to it.',
    goal: 'Define what you’re trying to understand before deciding what the observations might mean.',
    color: '#3b7dd8',
  },
  {
    roman: 'II',
    title: 'The Motor Planning Observation Map',
    intro:
      'Observing a child struggle with a movement is one thing. Organizing the information surrounding that performance is another. This volume gives you a visual framework for recording motor observations and exploring the task conditions in which they occur.',
    listLabel: 'You’ll learn to organize observations related to:',
    list: [
      'Motor imitation.',
      'Sequencing and action organization.',
      'Learning unfamiliar motor activities.',
      'Use of visual and verbal instructions.',
      'Differences between familiar and unfamiliar tasks.',
      'Task complexity and environmental demands.',
      'Level and type of assistance provided.',
      'Performance across different situations.',
    ],
    extraLabel: 'You’ll receive:',
    extra: [
      'Motor planning observation maps.',
      'Task analysis worksheets.',
      'Context and activity comparison sheets.',
      'Motor observation checklists.',
      'Performance documentation templates.',
      'Questions for further exploration.',
      'Visual reference charts.',
    ],
    note: 'The purpose isn’t to classify a child automatically. It’s to help you document what happens and identify relevant questions.',
    goal: 'Turn scattered observations into organized information you can review.',
    color: '#e8664f',
  },
  {
    roman: 'III',
    title: 'Explore Possible Praxis Patterns Without Jumping to Conclusions',
    intro:
      'A child may struggle with an unfamiliar action for several reasons. Task demands, prior experience, comprehension, attention, environmental conditions, and other factors may influence performance.',
    listLabel: 'I’ll help you organize your exploration of:',
    list: [
      'Repeated motor observations across activities.',
      'Differences between familiar and unfamiliar tasks.',
      'Imitation and sequencing demands.',
      'The influence of task complexity.',
      'Contextual factors that may affect performance.',
      'Possible praxis-related patterns worth exploring.',
      'Alternative explanations.',
      'Information that supports or challenges a working hypothesis.',
      'Questions that remain unanswered.',
    ],
    note: 'You’ll receive comparison matrices, reasoning worksheets, and visual frameworks — and learn why an isolated difficulty should not automatically be interpreted as evidence of a specific underlying mechanism.',
    goal: 'Explore possible patterns systematically while preserving uncertainty and considering alternative explanations.',
    color: '#2fa37a',
  },
  {
    roman: 'IV',
    title: 'The Praxis & Motor Planning Clinical Reasoning Matrix™',
    intro:
      'This is where the different pieces of information come together. Instead of treating every motor observation as an isolated event, you’ll use a structured matrix to organize the relationships between functional concerns, task demands, observable performance, context, and possible interpretations.',
    listLabel: 'You’ll learn to organize:',
    list: [
      'The primary functional concern.',
      'Observable motor behaviors.',
      'Task demands and instructions.',
      'Relevant environmental conditions.',
      'Differences across activities.',
      'Possible praxis-related questions.',
      'Missing information.',
      'Alternative hypotheses.',
      'Supporting and conflicting observations.',
      'Next questions to investigate.',
    ],
    note: 'You’ll also receive the Praxis Decision Matrix™, a visual worksheet designed to help you see what your current information supports, what remains uncertain, and what deserves further exploration.',
    goal: 'Make your reasoning easier to organize, review, and explain.',
    color: '#8a5cd1',
  },
  {
    roman: 'V',
    title: 'From Motor Planning Observations to Functional Goals',
    intro:
      'Organizing motor observations is only part of the process. You also need to understand how the difficulty affects the child’s participation in meaningful activities — using a practical structure: FUNCTION → TASK → OBSERVABLE PERFORMANCE → MEASUREMENT.',
    listLabel: 'You’ll explore:',
    list: [
      'Everyday activities affected by motor difficulties.',
      'Meaningful participation priorities.',
      'Observable performance during specific tasks.',
      'Baseline information.',
      'Context-specific goals.',
      'Environmental and task adaptations to consider.',
      'Progress documentation.',
      'Goal review frameworks.',
      'Communication of functional outcomes.',
    ],
    note: 'Instead of vague statements such as “improve motor planning,” you’ll organize goals around meaningful activities and observable outcomes.',
    goal: 'Connect your observations with functional outcomes that can be described and monitored over time.',
    color: '#d9940f',
  },
  {
    roman: 'VI',
    title: 'The Complete Praxis & Motor Planning Clinical Pathway™',
    intro:
      'This final volume brings everything together in one repeatable educational workflow, guiding you through eight stages.',
    listLabel: 'The eight stages:',
    list: [
      'Define the functional concern.',
      'Organize relevant background information.',
      'Identify task demands.',
      'Document observable performance.',
      'Explore possible patterns and alternative explanations.',
      'Identify missing information and review working hypotheses.',
      'Connect the analysis with functional goals.',
      'Identify next steps for investigation and follow-up.',
    ],
    note: 'You’ll also learn to recognize when the available information is insufficient to support a stronger conclusion.',
    goal: 'Approach pediatric praxis and motor planning questions with an organized process instead of relying on memory alone.',
    color: '#2f93cc',
  },
];
